import { Fragment, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import styled from 'styled-components'
import Swal from 'sweetalert2'
import axios from 'axios'
import config from '../../../config'
import Cookies from 'js-cookie'
import { prizesShareCampaignSchema } from './validations'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, convertToRaw, ContentState } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { getPublicUrl, getUploadImageUrl, IMAGE_TYPE } from '../../../helpers/url'
import { useDispatch } from 'react-redux'

const prizes = [
    {
      label: 'รางวัลที่ 1',
      value: 'FIRST',
    },
    {
      label: 'รางวัลที่ 2',
      value: 'SECOND',
    },
    {
      label: 'รางวัลที่ 3',
      value: 'THIRD',
    },
    {
      label: 'รางวัลที่ 4',
      value: 'FOURTH',
    },
    {
      label: 'รางวัลที่ 5',
      value: 'FIFTH',
    },
    {
      label: 'รางวัลข้างเคียงรางวัลที่ 1',
      value: 'NEARBY',
    },
    {
      label: 'รางวัลเลขหน้า 3 ตัว',
      value: 'FIRST3DIGITS',
    },
    {
      label: 'รางวัลเลขท้าย 3 ตัว',
      value: 'LAST3DIGITS',
    },
    {
      label: 'รางวัลเลขท้าย 2 ตัว',
      value: 'LAST2DIGITS',
    },
];

const PrizesShareCampaignForm = ({ value }) => {

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(prizesShareCampaignSchema),
  })

  const dispatch = useDispatch()
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const imagePreview = watch('imageUrl')

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState)
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()))
    const textContent = editorState.getCurrentContent().getPlainText().trim()
    const finalContent = textContent === '' ? '' : htmlContent
    
    setValue('description', finalContent, { shouldDirty: true })
  }

  const onSubmit = async (data) => {
    await Swal.fire({
      title: 'บันทึกข้อมูล',
      text: 'คุณต้องการบันทึกข้อมูลหรือไม่',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await axios.post(
            `${config.apiAdminGateway}/api/v2/prizes/share/campaign`,
            {...data,
              limit: data.limit ? data.limit.toString() : "0",
            },
            {
              headers: { Authorization: `Bearer ${Cookies.get('accessToken')}` },
            },
          )

          if (res.data.status.code !== 0) {
            throw new Error('Error updating round close detail')
          }

          Swal.fire({
            title: 'บันทึกข้อมูลสำเร็จ',
            icon: 'success',
          })
          return true
        } catch (error) {
          console.error(error)
          Swal.showValidationMessage('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
          return false
        }
      },
    })
  }

  const handleChangeImage = (e) => {
    if (e.target.files.length === 0) {
      setValue('imageUrl', '')
      return
    }

    const file = e.target.files[0]

    if (!file.type.includes('jpeg') && !file.type.includes('jpg') && !file.type.includes('png')) {
      Swal.fire({
        icon: 'error',
        title: 'รูปแบบไฟล์ไม่ถูกต้อง',
        text: 'กรุณาเลือกไฟล์ JPG หรือ PNG เท่านั้น',
        showCloseButton: true,
      })
      e.target.value = ''
      setValue('imageUrl', '')
      return
    }

    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: 'ไฟล์มีขนาดใหญ่เกินไป',
        text: 'กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 5MB',
        showCloseButton: true,
      })
      e.target.value = ''
      setValue('imageUrl', '')
      return
    }

    const image = new Image()
    image.src = URL.createObjectURL(file)

    image.onload = async () => {
      if (image.width > 1200 || image.height > 1200) {
        Swal.fire({
          icon: 'error',
          title: 'ขนาดรูปภาพไม่ถูกต้อง',
          text: 'กรุณาเลือกรูปภาพที่มีขนาดไม่เกิน 1200 X 1200 พิกเซล',
          showCloseButton: true,
        })
        e.target.value = ''
        setValue('imageUrl', '')
        URL.revokeObjectURL(image.src)
        return
      }

      dispatch({ type: 'loading_show' })
      try {
        const imageType = file.type.includes('png') ? IMAGE_TYPE.PNG : IMAGE_TYPE.JPEG
        const url = await getUploadImageUrl(imageType)
        const imageUrl = getPublicUrl(url)

        const response = await fetch(url, {
          method: 'PUT',
          body: file,
        })

        if (!response.ok) throw new Error('Error uploading image')

        setValue('imageUrl', imageUrl)
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถอัพโหลดได้',
          showCloseButton: true,
        })
        setValue('imageUrl', '')
      } finally {
        dispatch({ type: 'loading_hide' })
        URL.revokeObjectURL(image.src)
      }
    }

    image.onerror = () => {
      Swal.fire({
        icon: 'error',
        title: 'ไฟล์รูปภาพไม่ถูกต้อง',
        text: 'กรุณาเลือกไฟล์รูปภาพที่ถูกต้อง',
        showCloseButton: true,
      })
      e.target.value = ''
      setValue('imageUrl', '')
      URL.revokeObjectURL(image.src)
    }
  }

  const getImage = (image) => {
    if (!image) return

    if (typeof image === 'string') {
      return image
    } else if (image?.length > 0) {
      return URL.createObjectURL(image[0])
    }
  }
  
  useEffect(() => {
    const fetchPrizesCampaignData = async () => {
      dispatch({ type: 'loading_show' })
      try {
        const response = await axios.get(
        `${config.apiAdminGateway}/api/v2/prizes/share/campaign?roundDate=MASTER`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('accessToken')}` },
        },
        )
        
        if (response.data.status.code !== 0) {
        console.error('Error fetching campaign data:', response.data.status.message)
        return
        }
  
        const campaignData = response.data.data
        if (!campaignData) return
  
        const { prizeTypes, limit, description, imageUrl } = campaignData
        
        if (prizeTypes) setValue('prizeTypes', prizeTypes)
        if (limit) setValue('limit', limit)
        if (imageUrl) setValue('imageUrl', imageUrl)
  
        if (description) {
        const contentBlock = htmlToDraft(description)
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
          const editorState = EditorState.createWithContent(contentState)
          setEditorState(editorState)
        }
        setValue('description', description)
        }
      } catch (error) {
        console.error('Error fetching campaign data:', error)
      } finally {
        dispatch({ type: 'loading_hide' })
      }
    }
    fetchPrizesCampaignData()
  }, [value, setValue, dispatch])

  return (
    <div>
      <Form as="form" onSubmit={handleSubmit(onSubmit)}>
        <CheckboxGroup>
          <CheckboxGroupTitle>เลือกรางวัล</CheckboxGroupTitle>
          {prizes.map((prize) => (
            <CheckboxContainer key={prize.value}>
              <Checkbox 
                type="checkbox" 
                id={prize.value} 
                value={prize.value}
                {...register(`prizeTypes`)} 
              />
              <label htmlFor={prize.value}>{prize.label}</label>
            </CheckboxContainer>
          ))}
          {errors.prizeTypes && <ErrorMessageText>{errors.prizeTypes.message}</ErrorMessageText>}
        </CheckboxGroup>
        <InputContainer>
          <label htmlFor="limit">
            ระบุจำนวนผู้โชคดี
          </label>
          <StyledInput
            type="number"
            id="limit"
            min="1"
            pattern="\d*"
            onWheel={e => e.target.blur()}
            onInput={e => {
              e.target.value = e.target.value.replace(/\D/g, '');
            }}
            {...register('limit')}
          />
          {errors.limit && <ErrorMessageText>{errors.limit.message}</ErrorMessageText>}
        </InputContainer>
        <InputContainer>
          <label>
            รูป
            <span style={{ color: 'red', fontSize: '12px', fontWeight: 'normal', marginLeft: '4px' }}>
              (เฉพาะไฟล์ .jpg หรือ .jpeg และขนาดต้องไม่เกิน 1200 X 1200)
            </span>
          </label>
          <input
            type="file"
            id="imageUpload"
            accept="image/jpeg, image/jpg, image/png"
            {...register('imageUrl', { onChange: handleChangeImage })}
            style={{ display: 'none' }}
          />
          <div style={{ position: 'relative' }}>
            {!imagePreview ? (
              <ImageUploadLabel 
                htmlFor="imageUpload"
                style={{ 
                  cursor: 'pointer',
                  opacity: 1 
                }}
              >
                อัปโหลดรูปภาพ
              </ImageUploadLabel>
            ) : (
              <Fragment>
                <img src={getImage(imagePreview)} alt="Preview" style={{ width: '100%', height: '100%' }} />
                <CloseButton type="button" onClick={() => setValue('imageUrl', '')}>
                  X
                </CloseButton>
              </Fragment>
            )}
          </div>
          {errors.image && <ErrorMessageText>{errors.image.message}</ErrorMessageText>}
        </InputContainer>
        <InputContainer>
          <label htmlFor="description">เงื่อนไขในการเข้าร่วม</label>
          <EditorWrapper>
            <Editor
              toolbar={{
                options: ['inline', 'fontSize', 'colorPicker', 'textAlign', 'list', 'link', 'history'],
                inline: {
                  options: ['bold', 'italic', 'underline'],
                },
                textAlign: {
                  options: ['left', 'center', 'right', 'justify'],
                },
              }}
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              wrapperClassName="wrapper-class"
              editorClassName="editor-class"
              toolbarClassName="toolbar-class"
              stripPastedStyles={true}
              handlePastedText={() => false}
              placeholder="เขียนคำอธิบายแคมเปญ..."
            />
          </EditorWrapper>
          {errors.description && <ErrorMessageText>{errors.description.message}</ErrorMessageText>}
        </InputContainer>
        <SubmitButton disabled={!isDirty}>
          บันทึก
        </SubmitButton>
      </Form>
    </div>
  )
}

export default PrizesShareCampaignForm

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  -webkit-appearance: none;
  margin: 0;
  &:focus {
    border-color: #007bff;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const Checkbox = styled.input`
  min-width: 1.5rem;
  margin-bottom: 8px;
  aspect-ratio: 1;
  &::after {
    display: none !important;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  label {
    cursor: pointer;
    font-weight: 500;
    color: #333;
    user-select: none;
  }
  
  &:has(input:disabled) {
    opacity: 0.6;
    
    &:hover {
      background-color: transparent;
    }
    
    label {
      cursor: not-allowed;
      color: #999;
    }
  }
`

const CheckboxGroupTitle = styled.h5`
  font-weight: 600;
  color: #7e7e7e;
`

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: red;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  aspect-ratio: 1;
`

const ImageUploadLabel = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  background-color: #f2f2f2;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #e0e0e0;
  }
`

const ErrorMessageText = styled.span`
  color: red;
  font-size: 0.8rem;
`

const SubmitButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #cccccc;
    color: #888888;
    cursor: not-allowed;
  }
`

const EditorWrapper = styled.div`
  .wrapper-class {
    border: 1px solid #e0e0e0;
    border-radius: 0.5rem;
    overflow: hidden;
    height: 600px;
    &.disabled {
      opacity: 0.6;
      pointer-events: none;
      background-color: #f5f5f5;
    }
  }
  
  .toolbar-class {
    border: none;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
    padding: 0.5rem;
  }
  
  .editor-class {
    padding: 0.75rem;
    height: 600px;
    font-size: 1rem;
    line-height: 1.5;
    font-family: 'Anuphan', sans-serif;
    overflow-y: auto;
    
    &:focus {
      outline: none;
    }
  }
`
